import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from '../../lib/utils'

const buttonVariants = cva(
  "inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 active:scale-[0.98]",
  {
    variants: {
      variant: {
        default: "bg-primary-dark text-primary-dark-foreground hover:bg-primary-dark/90 hover:shadow-md",
        /** Main CTA — follows the active brand/theme primary token. */
        primary:
          "bg-primary text-primary-foreground hover:bg-primary/90 text-base",
        destructive:
          "bg-destructive text-white hover:bg-destructive/90",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground hover:border-accent-foreground/20",
        secondary:
          "bg-transparent text-foreground dark:text-cta border-[1.5px] border-cta hover:bg-cta/10",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-foreground dark:text-primary underline-offset-4 hover:underline",
        /** Gradient CTA for upgrade/upsell flows */
        upgrade:
          "bg-gradient-to-r from-primary to-primary-dark text-white dark:text-black hover:opacity-90",
        /** Inverted button for use on media/image overlays */
        media:
          "bg-media-foreground text-media hover:bg-media-foreground/90",
        /** Glass-morphism button for translucent overlays */
        glass:
          "bg-card/60 backdrop-blur-md hover:bg-card/80 text-foreground border border-border/50",
        /** Mint CTA — always #b4f9db. Use on dark surfaces (auth pages, hero sections) */
        cta:
          "bg-cta text-cta-foreground hover:bg-cta/90",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 px-3",
        lg: "h-11 px-8",
        icon: "h-10 w-10",
      },
      shape: {
        default: "",
        pill: "rounded-[6px]",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      shape: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
  VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, shape, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, shape, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }

